import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  Camera,
  Zap,
  Shield,
  Wifi,
  PlayCircle,
  ArrowRight,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import device1 from "../img/device1.jpeg";
import device2 from "../img/device2.jpeg";
import device3 from "../img/device3.jpeg";
import { useTranslation } from "react-i18next";

// Optimized intersection observer hook
const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          observer.unobserve(entry.target); // Stop observing after animation
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -100px 0px", // Reduced margin
        ...options,
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [hasAnimated, options]);

  return [elementRef, isVisible];
};

const WizardDevice = () => {
  const { t } = useTranslation();
  const [hoveredImage, setHoveredImage] = useState(null);

  // Use a single intersection observer for the whole section
  const [sectionRef, sectionInView] = useIntersectionObserver();

  // Memoized data
  const deviceImages = useMemo(() => [
    {
      id: 1,
      src: device1,
      alt: "Wizard Device on Field",
      caption: t("home.wizard_device.device_images.caption_1"),
    },
    {
      id: 2,
      src: device2,
      alt: "Wizard Device Camera Setup",
      caption: t("home.wizard_device.device_images.caption_2"),
    },
    {
      id: 3,
      src: device3,
      alt: "Wizard Device in Action",
      caption: t("home.wizard_device.device_images.caption_3"),
    },
  ], [t]);

  const features = useMemo(() => [
    {
      icon: Camera,
      title: t("home.wizard_device.features.camera.title"),
      description: t("home.wizard_device.features.camera.description"),
    },
    {
      icon: Zap,
      title: t("home.wizard_device.features.zap.title"),
      description: t("home.wizard_device.features.zap.description"),
    },
    {
      icon: Wifi,
      title: t("home.wizard_device.features.wifi.title"),
      description: t("home.wizard_device.features.wifi.description"),
    },
    {
      icon: Shield,
      title: t("home.wizard_device.features.shield.title"),
      description: t("home.wizard_device.features.shield.description"),
    },
  ], [t]);

  // Optimized hover handlers
  const handleMouseEnter = useCallback((id: any) => {
    setHoveredImage(id);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredImage(null);
  }, []);

  // Optimized background styles
  const backgroundStyles = useMemo(() => ({
    position: 'relative' as const,
    backgroundColor: '#ffffff',
    backgroundImage: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 25%, #e0f2fe 100%)',
    overflow: 'hidden',
  }), []);

  return (
    <div
      //@ts-ignore
      ref={sectionRef}
      style={backgroundStyles}
    >
      {/* Simplified background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-48 h-48 bg-blue-200 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 right-10 w-64 h-64 bg-purple-100 rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Left Column - Content */}
          <div className="lg:col-span-5 space-y-6">
            {/* Header Card */}
            <div className={`relative bg-gradient-to-br from-slate-800 via-blue-800 to-indigo-800 rounded-2xl p-6 shadow-lg transition-all duration-500 ${sectionInView ? "opacity-100" : "opacity-0"
              }`}>
              <div className="flex justify-end mb-4">
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
                  <Sparkles className="w-3 h-3 text-blue-300" />
                  <span className="text-xs font-medium text-blue-200">
                    {t("home.wizard_device.pro_device")}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="text-2xl lg:text-3xl font-bold text-white">
                  {t("home.wizard_device.title")}
                </h2>

                <div className="w-12 h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>

                <p className="text-base text-blue-100/90 leading-relaxed">
                  {t("home.wizard_device.description")}
                </p>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-3">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className={`bg-white rounded-xl p-4 shadow-md border border-gray-200 transition-all duration-300 hover:shadow-lg ${sectionInView ? "opacity-100" : "opacity-0"
                      }`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* CTA Section */}
            {/* <div className={`transition-all duration-500 ${sectionInView ? "opacity-100" : "opacity-0"}`}>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">
                      {t("home.wizard_device.cta.title")}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {t("home.wizard_device.cta.subtitle")}
                    </p>
                  </div>
                  <button
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium shadow hover:shadow-md transition-shadow"
                  >
                    <span className="flex items-center gap-1 text-sm">
                      {t("home.wizard_device.cta.button")}
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </button>
                </div>
              </div>
            </div> */}
          </div>

          {/* Right Column - Images */}
          <div className="lg:col-span-7">
            <div className={`transition-all duration-500 ${sectionInView ? "opacity-100" : "opacity-0"}`}>
              {/* Image Grid */}
              <div className="grid grid-cols-12 gap-3">
                {/* Small Images Column */}
                <div className="col-span-5 space-y-3">
                  {deviceImages.slice(0, 2).map((image) => (
                    <div
                      key={image.id}
                      className="group relative overflow-hidden rounded-lg shadow-md"
                      onMouseEnter={() => handleMouseEnter(image.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="aspect-[4/3] bg-slate-800 relative overflow-hidden">
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />

                        {/* Overlay */}
                        <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent transition-opacity duration-300 ${hoveredImage === image.id ? "opacity-100" : "opacity-0"
                          }`}>
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-white font-medium text-xs truncate">
                              {image.caption}
                            </p>
                            {/* <div className="flex items-center gap-1 mt-1">
                              <PlayCircle className="w-3 h-3 text-white/80" />
                              <span className="text-white/80 text-xs">
                                {t("home.wizard_device.right_columns.view_action")}
                              </span>
                            </div> */}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Large Image Column */}
                <div className="col-span-7">
                  <div
                    className="group relative overflow-hidden rounded-lg shadow-lg h-full"
                    onMouseEnter={() => handleMouseEnter(3)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="aspect-[3/4] lg:h-full bg-slate-800 relative overflow-hidden">
                      <img
                        src={deviceImages[2].src}
                        alt={deviceImages[2].alt}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />

                      {/* Main Image Overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-300 ${hoveredImage === 3 ? "opacity-100" : "opacity-0"
                        }`}>
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="text-white font-semibold text-sm mb-1">
                            {t("home.wizard_device.main_image.title")}
                          </h3>
                          <p className="text-white/90 text-xs mb-2">
                            {t("home.wizard_device.main_image.subtitle")}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-400" />
                              <span className="text-white/80 text-xs">
                                {t("home.wizard_device.main_image.features.4k_recording")}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-blue-400" />
                              <span className="text-white/80 text-xs">
                                {t("home.wizard_device.main_image.features.ia_analysis")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Floating Badge */}
                      <div className={`absolute top-3 right-3 transition-opacity duration-300 ${sectionInView ? "opacity-100" : "opacity-0"
                        }`}>
                        <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow">
                          {t("home.wizard_device.main_image.live")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(WizardDevice);